<template>
  <div class="page ability-cards-page">
    <van-nav-bar title="人员能力卡片" left-arrow @click-left="$router.back()" fixed placeholder class="top-nav"/>

    <section class="date-bar">
      <button class="date-arrow" @click="shiftDate(-1)">
        <van-icon name="arrow-left"/>
      </button>
      <span class="date-text" @click="showPicker = true">{{ displayDate }}</span>
      <button class="date-arrow" :disabled="isToday" @click="shiftDate(1)">
        <van-icon name="arrow"/>
      </button>
    </section>

    <van-loading v-if="loading" class="page-loading" color="#d4a24e">加载中</van-loading>
    <div v-else-if="!employees.length" class="empty-page">暂无员工数据</div>

    <van-swipe
      v-else
      ref="swipe"
      class="card-swipe"
      :loop="false"
      :show-indicators="false"
      :initial-swipe="0"
      @change="onSwipeChange"
    >
      <van-swipe-item v-for="(emp, idx) in employees" :key="emp.employeeId" class="card-swipe-item">
        <div class="scroll-card">
          <div class="ability-card" :class="['tier-' + emp.tierKey]">
            <div class="card-header">
              <div class="badge">{{ emp.tierLabel }}</div>
              <div class="avatar-wrap">
                <img v-if="emp.userHeadImg" :src="emp.userHeadImg" class="avatar-img" alt="头像" draggable="false"/>
                <span v-else class="avatar-text">{{ avatarText(emp.employeeName) }}</span>
              </div>
              <div class="header-info">
                <div class="name">{{ emp.employeeName || '-' }}</div>
                <div class="meta">{{ emp.departmentName || '-' }} {{ emp.positionName || '-' }}</div>
              </div>
              <div class="tier-mark">{{ emp.tierKey }}</div>
            </div>

            <div class="score-panel">
              <div class="score-label">综合评分</div>
              <div class="score-value">{{ formatScore(emp.abilityTotalScore) }}<small>分</small></div>
              <div class="score-note">已按岗位系数折算</div>
            </div>

            <div class="mini-cards">
              <div class="mini-card quant">
                <div class="mini-title"><van-icon name="chart-trending-o"/> 量化评分</div>
                <div class="mini-body">
                  <div><span>完成度</span><b>{{ quantCompletionText(emp) }}</b></div>
                  <div><span>权重</span><b>90%</b></div>
                </div>
              </div>
              <div class="mini-card nonquant">
                <div class="mini-title"><van-icon name="comment-o"/> 非量化评分</div>
                <div class="mini-body">
                  <div><span>分数</span><b>{{ formatScore(nonQuantScore(emp)) }}</b></div>
                  <div><span>权重</span><b>5%</b></div>
                </div>
              </div>
              <div class="mini-card star">
                <div class="mini-title"><van-icon name="star-o"/> 五星上将</div>
                <div class="mini-body">
                  <div><span>分数</span><b>{{ formatScore(emp.starScore) }}</b></div>
                  <div><span>权重</span><b>5%</b></div>
                </div>
              </div>
            </div>

            <div class="detail-lists">
              <div class="detail-col quant">
                <h4>量化数据</h4>
                <div v-if="emp.quantMetrics && emp.quantMetrics.length" class="list">
                  <div v-for="(m, i) in emp.quantMetrics" :key="'q' + i">
                    <span>{{ m.name }}</span>
                    <b>{{ formatMetricValue(m) }}</b>
                  </div>
                </div>
                <p v-else class="empty-col">暂无量化数据</p>
              </div>
              <div class="detail-col nonquant">
                <h4>非量化评价</h4>
                <div v-if="emp.nonQuant" class="list">
                  <div><span>AI评分</span><b>{{ formatScore(emp.nonQuant.aiScoreAvg) }}</b></div>
                  <div><span>主管复核</span><b>{{ formatScore(emp.nonQuant.reviewScoreAvg) }}</b></div>
                  <div><span>综合</span><b>{{ formatScore(emp.nonQuantOverall) }}</b></div>
                </div>
                <p v-else class="empty-col">暂无评价</p>
              </div>
              <div class="detail-col star">
                <h4>能力表现</h4>
                <div v-if="emp.starDimensions && emp.starDimensions.length" class="list star-list">
                  <span v-for="(d, i) in emp.starDimensions" :key="'s' + i">{{ i + 1 }}.{{ d.name }}</span>
                </div>
                <p v-else class="empty-col">暂无能力标签</p>
              </div>
            </div>

            <div class="section-card log-card">
              <div class="section-header">
                <span class="section-title"><van-icon name="edit"/> 当天日志</span>
                <span class="section-date">{{ displayDate }}</span>
              </div>
              <p class="log-summary">{{ logSummary(emp) || '暂无日志内容' }}</p>
              <div v-if="emp.quantAvg != null" class="log-footer">
                <span>量化完成情况</span>
                <b>{{ Math.round(emp.quantAvg) }}%</b>
              </div>
            </div>

            <div class="section-card ai-card">
              <div class="section-header">
                <span class="section-title"><van-icon name="bulb-o"/> AI 建议</span>
                <span class="section-date">{{ displayDate }}</span>
              </div>
              <ol v-if="emp.aiDimensions && emp.aiDimensions.length" class="ai-list">
                <li v-for="(d, i) in emp.aiDimensions" :key="'ai' + i">{{ aiText(d) }}</li>
              </ol>
              <p v-else class="empty-col">暂无 AI 建议</p>
            </div>

            <div class="section-card guidance-card">
              <div class="section-header">
                <span class="section-title"><van-icon name="manager-o"/> Boss 指导意见</span>
                <span class="section-date">{{ displayDate }}</span>
              </div>
              <van-field
                v-model="emp.guidance"
                type="textarea"
                rows="3"
                placeholder="请补充或调整当日建议："
                class="guidance-field"
                :border="false"
              />
              <div class="guidance-actions">
                <button class="voice-btn" :class="{active: emp.voiceOpen}" @click="emp.voiceOpen = !emp.voiceOpen">
                  <van-icon name="volume-o"/> {{ emp.voiceOpen ? '收起语音' : '语音输入调整建议' }}
                </button>
                <button class="submit-btn" @click="submitGuidance(emp)">确认提交</button>
              </div>
              <boss-voice-input v-if="emp.voiceOpen" @confirmed="onVoiceConfirmed(emp, $event)"/>
            </div>
          </div>
        </div>
      </van-swipe-item>
    </van-swipe>

    <van-popup v-model="showPicker" position="bottom">
      <van-datetime-picker
        v-model="pickerDate"
        type="date"
        :max-date="new Date()"
        @confirm="onDateConfirm"
        @cancel="showPicker = false"
      />
    </van-popup>
  </div>
</template>

<script>
import coreapi from "@/utils/coreapi";
import BossVoiceInput from "@/components/boss/BossVoiceInput.vue";

export default {
  name: "BossAbilityCards",
  components: { BossVoiceInput },
  data() {
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
    return {
      loading: false,
      date: today,
      showPicker: false,
      pickerDate: now,
      employees: [],
      currentIndex: 0
    };
  },
  computed: {
    displayDate() {
      const d = new Date(`${this.date}T00:00:00`);
      const week = ["周日", "周一", "周二", "周三", "周四", "周五", "周六"][d.getDay()];
      return `${this.date} ${week}`;
    },
    isToday() {
      return this.date === this.formatDate(new Date());
    }
  },
  mounted() {
    this.load();
  },
  methods: {
    formatDate(date) {
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
    },
    shiftDate(delta) {
      const d = new Date(`${this.date}T00:00:00`);
      d.setDate(d.getDate() + delta);
      if (d > new Date()) return;
      this.date = this.formatDate(d);
      this.load();
    },
    onDateConfirm(val) {
      this.showPicker = false;
      this.date = this.formatDate(new Date(val));
      this.load();
    },
    avatarText(name) {
      return name ? name.charAt(0) : "员";
    },
    loadGuidance(emp) {
      // 已保存的指导意见由后端随能力卡片一并返回（boss_guidance 表），直接从卡片数据回填
      emp.guidance = (emp.cardData && emp.cardData.guidance) ? emp.cardData.guidance : "";
    },
    async load() {
      this.loading = true;
      try {
        const data = await coreapi.bossAllWorklogs(this.date);
        this.employees = (data || []).map(v => {
          let today = [], tomorrow = [], aiDimensions = [];
          const raw = v.fieldValuesJson;
          if (raw) {
            try {
              const outer = JSON.parse(raw);
              const inner = typeof outer.dailyLogs === "string" ? JSON.parse(outer.dailyLogs || "{}") : (outer.dailyLogs || {});
              today = (inner.today || []).filter(Boolean);
              tomorrow = (inner.tomorrow || []).filter(Boolean);
            } catch (e) {
              // ignore
            }
          }
          if (v.aiDimensionsJson) {
            try {
              const arr = JSON.parse(v.aiDimensionsJson);
              aiDimensions = (Array.isArray(arr) ? arr : []).map(d => ({
                name: (d.name || "").trim(),
                score: d.score,
                reason: d.reason || ""
              }));
            } catch (e) {
              // ignore
            }
          }
          return {
            ...v,
            today,
            tomorrow,
            aiDimensions,
            cardData: null,
            cardLoaded: false,
            cardLoading: false,
            quantMetrics: [],
            nonQuant: null,
            starDimensions: [],
            starScore: 0,
            nonQuantOverall: 0,
            quantAvg: 0,
            abilityTotalScore: 0,
            tierKey: "bronze",
            tierLabel: "青铜",
            guidance: "",
            voiceOpen: false
          };
        }).sort((a, b) => {
          const as = a.aiQualityScore != null ? a.aiQualityScore : (a.finalScore != null ? a.finalScore : -1);
          const bs = b.aiQualityScore != null ? b.aiQualityScore : (b.finalScore != null ? b.finalScore : -1);
          return bs - as;
        });
        this.currentIndex = 0;
        await this.ensureCardsAround(0);
      } catch (e) {
        this.$toast.fail(e.message || "加载失败");
      } finally {
        this.loading = false;
        this.$nextTick(() => {
          if (this.$refs.swipe && this.$refs.swipe.resize) {
            this.$refs.swipe.resize();
          }
        });
      }
    },
    async ensureCardsAround(index) {
      const targets = [index, index - 1, index + 1].filter(i => i >= 0 && i < this.employees.length);
      await Promise.all(targets.map(i => this.loadCard(i)));
    },
    async loadCard(index) {
      const emp = this.employees[index];
      if (!emp || emp.cardLoaded || emp.cardLoading) return;
      emp.cardLoading = true;
      try {
        const data = await coreapi.bossAbilityCard(emp.employeeId, this.date);
        emp.cardData = data || {};
        emp.quantMetrics = data && Array.isArray(data.quantMetrics) ? data.quantMetrics : [];
        emp.nonQuant = data && data.nonQuant ? data.nonQuant : null;
        emp.starDimensions = data && Array.isArray(data.starDimensions) ? data.starDimensions : [];
        emp.starScore = Number(data && data.starScore) || 0;
        emp.nonQuantOverall = (emp.nonQuant && emp.nonQuant.overallScore) ? emp.nonQuant.overallScore : 0;
        emp.quantAvg = this.computeQuantAvg(emp.quantMetrics);
        emp.abilityTotalScore = this.computeTotalScore(emp);
        const tier = this.abilityTier(emp.abilityTotalScore);
        emp.tierKey = tier.key;
        emp.tierLabel = tier.label;
        this.loadGuidance(emp);
      } catch (e) {
        // 单个员工能力卡片失败不阻断整体滑动
      } finally {
        emp.cardLoaded = true;
        emp.cardLoading = false;
      }
    },
    computeQuantAvg(metrics) {
      if (!metrics || !metrics.length) return 0;
      const sum = metrics.reduce((s, m) => s + (Number(m.completionRate) || 0), 0);
      return sum / metrics.length;
    },
    nonQuantScore(emp) {
      return emp.nonQuantOverall || 0;
    },
    computeTotalScore(emp) {
      const quant = emp.quantAvg || 0;
      const nonQuant = emp.nonQuantOverall || 0;
      const star = emp.starScore || 0;
      return quant * 0.9 + nonQuant * 0.05 + star * 0.05;
    },
    abilityTier(score) {
      if (score >= 90) return { key: "gold", label: "黄金" };
      if (score >= 60) return { key: "silver", label: "白银" };
      return { key: "bronze", label: "青铜" };
    },
    quantCompletionText(emp) {
      return `${Math.round(emp.quantAvg || 0)}%`;
    },
    formatScore(value) {
      if (value == null || isNaN(value)) return "0.0";
      return Number(value).toFixed(1);
    },
    formatMetricValue(m) {
      const actual = Number(m.actual);
      if (!isNaN(actual)) return `${Math.round(actual * 100) / 100}${m.unit || ""}`;
      return "-";
    },
    logSummary(emp) {
      if (emp.today && emp.today.length) return emp.today.join("；");
      return emp.fieldValuesJson ? "" : "";
    },
    aiText(d) {
      if (d.reason) return d.reason;
      return d.name ? `${d.name}${d.score != null ? "（" + d.score + "分）" : ""}` : "";
    },
    onSwipeChange(index) {
      this.currentIndex = index;
      this.ensureCardsAround(index);
    },
    async submitGuidance(emp) {
      const text = (emp.guidance || "").trim();
      try {
        await coreapi.bossSaveGuidance({ employeeId: emp.employeeId, date: this.date, guidance: text });
        if (emp.cardData) emp.cardData.guidance = text;
        this.$toast.success(text ? "指导意见已保存" : "已清空指导意见");
      } catch (e) {
        this.$toast.fail((e && e.message) || "保存失败");
      }
    },
    onVoiceConfirmed(emp, text) {
      const value = (text || "").trim();
      if (!value) return;
      emp.guidance = ((emp.guidance || "") + (emp.guidance ? "\n" : "") + value).trim();
      emp.voiceOpen = false;
    }
  }
};
</script>

<style lang="less" scoped>
@import "~@/assets/base.less";

.page {
  min-height: 100vh;
  background: linear-gradient(180deg, #fdf6e3 0%, #f8ecd0 100%);
}

.top-nav /deep/ .van-nav-bar {
  background: linear-gradient(135deg, #c89f4b, #e8c76c);
  color: #fff;
}

.top-nav /deep/ .van-nav-bar__title {
  color: #fff;
}

.top-nav /deep/ .van-icon {
  color: #fff;
}

.date-bar {
  position: fixed;
  top: 46px;
  left: 0;
  right: 0;
  z-index: 10;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(6px);
  box-shadow: 0 2px 8px rgba(168, 132, 55, 0.08);
}

.date-arrow {
  width: 36px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 0;
  background: transparent;
  color: #8c6d28;
  font-size: 18px;
}

.date-arrow:disabled {
  opacity: 0.35;
}

.date-text {
  flex: 1;
  text-align: center;
  color: #5e4b1f;
  font-size: 15px;
  font-weight: 500;
}

.page-loading,
.empty-page {
  margin-top: 120px;
  text-align: center;
  color: #9a7d3c;
}

.card-swipe {
  position: fixed;
  top: 90px;
  bottom: 0;
  left: 0;
  right: 0;
  touch-action: pan-y;
}

.card-swipe-item {
  height: 100%;
  padding: 12px;
  box-sizing: border-box;
  touch-action: pan-y;
}

.scroll-card {
  height: 100%;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y;
  overscroll-behavior-y: contain;
}

.ability-card {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 6px 20px rgba(160, 128, 56, 0.12);
  overflow: hidden;
}

.card-header {
  position: relative;
  padding: 24px 20px 16px;
  text-align: center;
  background: linear-gradient(180deg, #fcefc1 0%, #fff8e0 100%);
}

.tier-gold .card-header {
  background: linear-gradient(180deg, #fbe9a6 0%, #fff5d0 100%);
}

.tier-silver .card-header {
  background: linear-gradient(180deg, #e8e8e8 0%, #f6f6f6 100%);
}

.tier-bronze .card-header {
  background: linear-gradient(180deg, #f3dcc8 0%, #faf0e8 100%);
}

.badge {
  position: absolute;
  top: 14px;
  left: 14px;
  width: 46px;
  height: 46px;
  line-height: 42px;
  color: #b8860b;
  font-size: 13px;
  font-weight: 700;
  text-align: center;
  background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Cpath d='M50 2 L61 35 L96 35 L68 57 L79 91 L50 70 L21 91 L32 57 L4 35 L39 35 Z' fill='%23e8c76c' stroke='%23b8860b' stroke-width='2'/%3E%3C/svg%3E") no-repeat center/contain;
}

.avatar-wrap {
  width: 88px;
  height: 88px;
  margin: 0 auto;
  border-radius: 50%;
  overflow: hidden;
  border: 3px solid #fff;
  box-shadow: 0 4px 12px rgba(168, 132, 55, 0.22);
  background: #d4a24e;
}

.avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  -webkit-user-drag: none;
}

.avatar-text {
  display: block;
  width: 100%;
  height: 100%;
  line-height: 82px;
  color: #fff;
  font-size: 32px;
  font-weight: 700;
  text-align: center;
}

.header-info {
  margin-top: 12px;
}

.name {
  color: #3d2e0a;
  font-size: 22px;
  font-weight: 700;
}

.meta {
  margin-top: 6px;
  color: #7a6126;
  font-size: 13px;
}

.tier-mark {
  position: absolute;
  top: 16px;
  right: 16px;
  padding: 4px 10px;
  color: #b8860b;
  font-size: 12px;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.75);
  border-radius: 12px;
}

.score-panel {
  margin: 16px 16px 0;
  padding: 18px 0;
  text-align: center;
  background: #fffaf0;
  border-radius: 14px;
  border: 1px solid #f3e2b3;
}

.score-label {
  display: inline-block;
  padding: 4px 12px;
  color: #a47c20;
  font-size: 13px;
  background: #f8e9c2;
  border-radius: 12px;
}

.score-value {
  margin-top: 8px;
  color: #b8860b;
  font-size: 48px;
  font-weight: 700;
  line-height: 1;
}

.score-value small {
  margin-left: 4px;
  font-size: 16px;
  font-weight: 400;
}

.score-note {
  margin-top: 6px;
  color: #b8a06a;
  font-size: 12px;
}

.mini-cards {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin: 14px 16px 0;
}

.mini-card {
  padding: 12px 8px;
  border-radius: 12px;
  background: #fffbf0;
  border: 1px solid #f3e2b3;
}

.mini-card.quant {
  background: #f5f8ff;
  border-color: #d6e3ff;
}

.mini-card.nonquant {
  background: #f0f9f4;
  border-color: #cbead7;
}

.mini-card.star {
  background: #fff5f5;
  border-color: #f8d0d0;
}

.mini-title {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  color: #7a6126;
  font-size: 12px;
  font-weight: 600;
}

.mini-title .van-icon {
  font-size: 13px;
}

.mini-body {
  margin-top: 10px;
}

.mini-body > div {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
  font-size: 12px;
}

.mini-body > div:last-child {
  margin-bottom: 0;
}

.mini-body span {
  color: #9e8a5a;
  font-size: 11px;
}

.mini-body b {
  color: #5e4b1f;
  font-size: 14px;
  font-weight: 700;
}

.mini-card.quant b {
  color: #2f6bd0;
}

.mini-card.nonquant b {
  color: #2e9b68;
}

.mini-card.star b {
  color: #c6282d;
}

.detail-lists {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin: 14px 16px 0;
}

.detail-col {
  padding: 12px 8px;
  border-radius: 12px;
  background: #fffbf0;
  border: 1px solid #f3e2b3;
  min-height: 120px;
}

.detail-col h4 {
  margin: 0 0 10px;
  color: #a47c20;
  font-size: 12px;
  font-weight: 600;
}

.detail-col.quant h4 {
  color: #2f6bd0;
}

.detail-col.nonquant h4 {
  color: #2e9b68;
}

.detail-col.star h4 {
  color: #c6282d;
}

.list > div,
.list > span {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 6px;
  font-size: 12px;
  line-height: 1.4;
}

.list span {
  color: #7d6a45;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.list b {
  color: #5e4b1f;
  font-weight: 600;
  margin-left: 6px;
}

.star-list {
  flex-direction: column;
  align-items: flex-start !important;
}

.star-list span {
  white-space: normal;
  margin-bottom: 4px;
  color: #5e4b1f;
}

.empty-col {
  margin: 0;
  color: #c4b58e;
  font-size: 12px;
}

.section-card {
  margin: 14px 16px 0;
  padding: 16px;
  border-radius: 14px;
  background: #fffaf0;
  border: 1px solid #f3e2b3;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #a47c20;
  font-size: 15px;
  font-weight: 700;
}

.section-title .van-icon {
  font-size: 18px;
}

.section-date {
  color: #b8a06a;
  font-size: 12px;
}

.log-summary {
  margin: 0;
  color: #5e4b1f;
  font-size: 13px;
  line-height: 1.7;
}

.log-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px dashed #ead6a6;
  color: #7a6126;
  font-size: 13px;
}

.log-footer b {
  color: #b8860b;
  font-size: 16px;
}

.ai-list {
  margin: 0;
  padding-left: 16px;
  color: #5e4b1f;
  font-size: 13px;
  line-height: 1.7;
}

.ai-list li {
  margin-bottom: 6px;
}

.guidance-field {
  padding: 0;
  background: #fff;
  border-radius: 10px;
  overflow: hidden;
}

.guidance-field /deep/ .van-field__control {
  padding: 10px;
  font-size: 13px;
  line-height: 1.6;
}

.guidance-actions {
  display: flex;
  gap: 10px;
  margin-top: 12px;
}

.voice-btn,
.submit-btn {
  flex: 1;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  border: 0;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
}

.voice-btn {
  color: #7a6126;
  background: #f8e9c2;
}

.submit-btn {
  color: #fff;
  background: linear-gradient(135deg, #c89f4b, #e8c76c);
}

.scroll-card::-webkit-scrollbar {
  display: none;
}

/* 底部留间距，避免最后一个卡片贴底 */
.ability-card {
  padding-bottom: 24px;
}
</style>



// WEBPACK FOOTER //
// src/views/boss/AbilityCards.vue